import type { OrderByDescriptor } from '../api/types/common/OrderByDescriptor';
import type { PaginationResponse } from '../api/types/common/PaginationResponse';

export const PAGINATED_DATA_ENTITIES = [
  'TopCategory',
  'SecondaryCategory',
  'ProductType',
  'SpecConstraint',
] as const;

export type PaginatedDataEntityKey = (typeof PAGINATED_DATA_ENTITIES)[number];

export type DataManagementSortOption =
  | 'name_asc'
  | 'name_desc'
  | 'created_desc'
  | 'created_asc';

export const DEFAULT_DATA_MANAGEMENT_PAGE_SIZE = 20;
export const DATA_MANAGEMENT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export function isPaginatedDataEntity(entityKey: string): entityKey is PaginatedDataEntityKey {
  return (PAGINATED_DATA_ENTITIES as readonly string[]).includes(entityKey);
}

export function sortOptionToOrderBy(
  sort: DataManagementSortOption,
  entityKey: PaginatedDataEntityKey
): OrderByDescriptor[] {
  const nameField = entityKey === 'SpecConstraint' ? 'DisplayName' : 'Name';
  switch (sort) {
    case 'name_asc':
      return [{ field: nameField, descending: false }];
    case 'name_desc':
      return [{ field: nameField, descending: true }];
    case 'created_desc':
      return [{ field: 'CreatedAt', descending: true }];
    case 'created_asc':
      return [{ field: 'CreatedAt', descending: false }];
    default:
      return [{ field: nameField, descending: false }];
  }
}

export type PaginatedEntityQueryOptions = {
  pageNumber: number;
  pageSize: number;
  searchText: string;
  parentFilterId?: number;
  sort: DataManagementSortOption;
  isDeleted?: boolean;
};

export function buildPaginatedEntityQuery(
  entityKey: PaginatedDataEntityKey,
  opts: PaginatedEntityQueryOptions
): Record<string, unknown> {
  const base = {
    pagination: { pageNumber: opts.pageNumber, pageSize: opts.pageSize },
    orderBy: sortOptionToOrderBy(opts.sort, entityKey),
    isDeleted: opts.isDeleted,
  };
  const trimmedSearch = opts.searchText.trim();

  switch (entityKey) {
    case 'TopCategory':
      return { ...base, name: trimmedSearch || undefined };
    case 'SecondaryCategory':
      return {
        ...base,
        name: trimmedSearch || undefined,
        topCategoryId: opts.parentFilterId,
      };
    case 'ProductType':
      return {
        ...base,
        name: trimmedSearch || undefined,
        secondaryCategoryId: opts.parentFilterId,
      };
    case 'SpecConstraint':
      return {
        ...base,
        search: trimmedSearch || undefined,
        productTypeId: opts.parentFilterId,
      };
    default:
      return base;
  }
}

export function parseEntityList<T>(data: unknown): T[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as T[];
  if (typeof data === 'object') {
    const record = data as { items?: T[] };
    if (Array.isArray(record.items)) return record.items;
  }
  return [];
}

export function readPaginationMeta(
  pagination?: PaginationResponse | null,
  fallbackPageSize = DEFAULT_DATA_MANAGEMENT_PAGE_SIZE
): { pageNumber: number; pageSize: number; totalPages: number } {
  return {
    pageNumber: pagination?.pageNumber ?? 1,
    pageSize: pagination?.pageSize ?? fallbackPageSize,
    totalPages: pagination?.totalPageCount ?? 1,
  };
}

const SORT_LABELS: Record<DataManagementSortOption, string> = {
  name_asc: 'Ada (A-Z)',
  name_desc: 'Ada (Z-A)',
  created_desc: 'En yeni',
  created_asc: 'En eski',
};

export function hasActiveDataManagementFilters(
  searchText: string,
  parentFilterId: number | undefined,
  sort: DataManagementSortOption
): boolean {
  return searchText.trim() !== '' || parentFilterId != null || sort !== 'name_asc';
}

export function getActiveDataManagementFilterCount(
  searchText: string,
  parentFilterId: number | undefined,
  sort: DataManagementSortOption
): number {
  let count = 0;
  if (searchText.trim() !== '') count += 1;
  if (parentFilterId != null) count += 1;
  if (sort !== 'name_asc') count += 1;
  return count;
}

export function getDataManagementSortLabel(sort: DataManagementSortOption): string {
  return SORT_LABELS[sort];
}
